import type { Map } from "ol";
import { transform } from "ol/proj";
import { cursorStyleFunctions, findMaxEventYearFeature, getImageScale } from "../../helpers";
import { getItemWebStorage, setItemSessionStorage } from "../../../../../features/webStorageSaver";
import { store } from "../../../../../stateManagement/store";
import type TileLayer from "ol/layer/Tile";
import type { TileWMS } from "ol/source";
import { setRosreestrObjectsInfoIsLoading, setRosreestrObjectsInfoModalIsShown } from "../../../../../stateManagement/reducers/RosreestrSlice";
import { getRosreestrObjectsInfo } from "../rosreestrFunctions/rosreestrFunctions";
import { showToastMessage } from "../../../../../stateManagement/reducers";
import { rosreestrGetObjectsOnMapPoint } from "./handleFeature/rosreestr";
import axios from "axios";
import { handleFeatureRoadWeatherStation } from "./handleFeature/roadWeatherStation";
import { handleFeatureCameras } from "./handleFeature/cameras";
import { handleFeatureDefault } from "./handleFeature/default";
import { navigateTo } from "../../../../navigationService/navigationServiceFunctions";
import { setFeaturesOnMapPoint } from "../../../../../stateManagement/reducers/ObjectsOnMapPointInfoSlice";

interface IGetFeatureInfoParams {
    VERSION: string;
    LAYERS: string;
    CQL_FILTER: string | null;
    featureCount?: any;
    styles?: string;
    propertyName?: string;
    buffer?: any;
    bbox?: any;
}

const MAPS_WITH_SINGLE_FEATURE_HANDLE = ['Weather', 'Cameras']
const MAPS_WITH_MAX_EVENT_YEAR_FEATURE_HANDLE = ['RoadDiagnosticsSegments', 'RoadWorks', 'RoadWorksWithNormativities']
const FEATURE_PROPS_FOR_MAPS: any = {
    RoadDiagnosticsSegments: 'ObjectTypeString,PathName,InfoObjectID,RoadName,PathPosition,PathEnding,EventYear,EndDate',
}

export async function getObjectsOnMapPoint(event: any, mapName: string, layer: TileLayer<TileWMS>) {
    const { resolution, projection } = store.getState().map.mapPosition ?? {}
    const coordinate = event?.coordinate
    const isRosreestrEnabled = store.getState().contentHeader.rosreestrIsEnabled
    if (isRosreestrEnabled) return rosreestrGetObjectsOnMapPoint(coordinate);
    if (!layer) return;
    cursorStyleFunctions.loading();
    const source = layer?.getSource()
    const layerParams = source?.getParams()
    const { VERSION, LAYERS, CQL_FILTER } = layerParams ?? {}
    let featureInfoUrlParams = getFeatureURLParamsObject({ VERSION, LAYERS, CQL_FILTER, propertyName: FEATURE_PROPS_FOR_MAPS[mapName] })
    let featureInfoUrl = source?.getFeatureInfoUrl(coordinate, resolution, projection, featureInfoUrlParams)
    if (!featureInfoUrl) return;
    const dispatch = store.dispatch
    try {
        const response = await axios.get(featureInfoUrl)
        const data = response?.data
        let features = data?.features ?? []
        if (features.length > 0) {
            const objectFeatures = filterObjectFeatures(features, mapName)
            clearFeaturesGeometry(objectFeatures)
            const regionFeatures = filterRegionFeatures(features)
            const federalRoadFeatures = filterFederalRoadFeatures(features)
            dispatch(setFeaturesOnMapPoint({ objectFeatures, regionFeatures, federalRoadFeatures }))
            setItemSessionStorage('featuresOnMapPoint', { objectFeatures, regionFeatures, federalRoadFeatures })
            if (objectFeatures.length === 1 || MAPS_WITH_SINGLE_FEATURE_HANDLE.includes(mapName)) {
                const objectFeature = objectFeatures[0]
                handleSingleFeature(objectFeature, mapName)
            } else {
                const [lon, lat] = transform(event.coordinate, 'EPSG:3857', 'EPSG:4326')
                    .map(coord => coord?.toFixed(7));
                navigateTo(`/app/objectsList?lon=${lon}&lat=${lat}`);
            }
        } else {
            dispatch(showToastMessage({ messageText: 'Нет объектов в точке на карте', messageType: "info", windowType: "map" }))
        }
    } catch (error) {
        console.error('GetFeatureInfo error:', error);
        dispatch(showToastMessage({ messageText: 'Ошибка получения данных с карты', messageType: "error", windowType: "map" }))
    }
    cursorStyleFunctions.default();
}

function handleSingleFeature(feature: any, mapName: string) {
    if (mapName === "Weather") {
        return handleFeatureRoadWeatherStation(feature)
    }
    if (mapName === "Cameras") {
        return handleFeatureCameras(feature)
    }
    handleFeatureDefault(feature)
}

function clearFeaturesGeometry(features: any[]) {
    features.forEach(feature => {
        delete feature.geometry
        delete feature.properties.GeometryText
    })
}

function filterObjectFeatures(features: any[], mapName: string): any[] {
    let basicFeatures = features.filter((f: any) => !f.id.includes('OSM') && !f.id.includes('Region'))
    if (mapName === "Weather") {
        return basicFeatures.filter(f => f.id.includes('RoadWeatherStation'))
    }
    if (mapName === "Cameras") {
        return basicFeatures.filter(f => f.id.includes('RoadWebCameras'))
    }
    if (MAPS_WITH_MAX_EVENT_YEAR_FEATURE_HANDLE.includes(mapName)) {
        if (['RoadWorks', 'RoadWorksWithNormativities'].includes(mapName)) {
            basicFeatures = basicFeatures.filter(f => f.id.includes("Work"))
        }
        return [findMaxEventYearFeature(basicFeatures)];
    }
    return basicFeatures
}

function filterRegionFeatures(features: any[]): any[] {
    let regionFeatures = features.filter((f: any) => f.id.includes('Region'))
    return regionFeatures
}

function filterFederalRoadFeatures(features: any[]): any[] {
    let federalRoadFeatures = features.filter((f: any) => f.id.includes('OSMFederalRoads'))
    return federalRoadFeatures
}

export async function getObjectsOnCustomMapPoint(event: any, mapId: any, objectType: string, layer: TileLayer<TileWMS>, navigate: any, map?: Map) {
    cursorStyleFunctions.loading();
    const viewResolution: any = map?.getView()?.getResolution();
    const viewProjection: any = map?.getView()?.getProjection();
    const zoom: any = map?.getView()?.getZoom();
    const projectionCode: any = viewProjection?.getCode()
    const isRosreestrEnabled = store.getState().contentHeader.rosreestrIsEnabled
    if (isRosreestrEnabled) {
        const rosreestrObjectsInfoIsLoading = store.getState().rosreestr.rosreestrObjectsInfoIsLoading
        if (!rosreestrObjectsInfoIsLoading) {
            cursorStyleFunctions.loading();
            store.dispatch(setRosreestrObjectsInfoIsLoading(true))
            store.dispatch(setRosreestrObjectsInfoModalIsShown(true))
            await getRosreestrObjectsInfo(event?.coordinate, zoom, projectionCode)
            store.dispatch(setRosreestrObjectsInfoIsLoading(false))
            cursorStyleFunctions.default();
        }
    } else if (layer) {
        const { cardAdditionalItems, mapAdditionalItems } = getItemWebStorage('currentConfig')
        const clickedCoordinate = transform(event.coordinate, 'EPSG:3857', 'EPSG:4326');
        let mapAdditionalItem = mapAdditionalItems.find((item: any) => item.id === mapId)
        const { id, getFeatureInfoOptions, objectsList } = mapAdditionalItem ?? {}
        const { propertyForEntityId, requiredProperties, initCQLFilter, propertyForPreventDoubles } = getFeatureInfoOptions ?? {}
        let layersNames = layer?.getSource()?.getParams()?.LAYERS
        let cqlFilter = layer?.getSource()?.getParams()?.CQL_FILTER
        let cqlFilterArray: any = []
        if (initCQLFilter) {
            cqlFilterArray.push(initCQLFilter)
        }
        if (cqlFilter) {
            cqlFilterArray.push(cqlFilter)
        }
        let styles = layer?.getSource()?.getParams()?.STYLES
        let wmsVersion = layer?.getSource()?.getParams()?.VERSION
        let featureUrlParams = getFeatureURLParamsObject(
            {
                VERSION: wmsVersion,
                LAYERS: layersNames,
                CQL_FILTER: cqlFilterArray.length > 0 ? cqlFilterArray.join(' and ') : null,
                propertyName: requiredProperties,
                styles
            })
        let featureInfoUrl = layer?.getSource()?.getFeatureInfoUrl(
            event.coordinate,
            viewResolution,
            viewProjection,
            featureUrlParams
        )
        if (featureInfoUrl) {
            fetch(featureInfoUrl)
                .then((response) => response.json())
                .then((data) => {
                    cursorStyleFunctions.default();
                    let objectsFeatures: any = []
                    objectsFeatures = data.features.filter((feature: any) => !feature.id.includes('OSM') && !feature.id.includes('Region'))
                    if (propertyForPreventDoubles) {
                        let featuresGroups = Object.groupBy(objectsFeatures, (object: any) => object.properties[propertyForPreventDoubles])
                        objectsFeatures = Object.values(featuresGroups).map((featureGroup: any) => featureGroup[0])
                    }
                    setItemSessionStorage('customMap.features', objectsFeatures);
                    let cardUrl, objectsListUrl
                    if (cardAdditionalItems) {
                        let cardAdditionalItem = cardAdditionalItems.find((item: any) => item.id === id)
                        cardUrl = cardAdditionalItem?.url
                        objectsListUrl = objectsList?.url
                    }
                    if (objectsFeatures.length == 0) {
                        store.dispatch(showToastMessage({ messageText: 'В данной точке отсутствуют объекты', messageType: "info", windowType: "map" }))
                    }
                    else {
                        if (objectsFeatures.length == 1) {
                            let feature = objectsFeatures[0]
                            let entityId = propertyForEntityId ? feature?.properties[propertyForEntityId] : feature?.id.split('.')[1]
                            if (cardUrl) {
                                cardUrl = cardUrl.replace(':id', `${id}`)
                                cardUrl = cardUrl.replace(':objectType', `${objectType}`)
                                cardUrl = cardUrl.replace(':entityId', `${entityId}`)
                                navigate(`/app/${cardUrl}`)
                            }
                        } else {
                            const [longitude, latitude] = clickedCoordinate.map(coord => coord?.toFixed(7));
                            if (objectsListUrl) {
                                objectsListUrl = objectsListUrl.replace(':id', `${id}`)
                                objectsListUrl = objectsListUrl.replace(':objectType', `${objectType}`)
                                navigate(`/app/${objectsListUrl}?lon=${longitude}&lat=${latitude}`)
                            }
                        }
                    }
                })
                .catch((error) => {
                    cursorStyleFunctions.default();
                    console.error('GetFeatureInfo error:', error);
                    store.dispatch(showToastMessage({ messageText: 'Ошибка получения данных с карты', messageType: "error", windowType: "map" }))
                });
        }
    }
}

export function getFeatureURLParamsObject({ VERSION, LAYERS, styles, CQL_FILTER, propertyName, buffer, bbox, featureCount }: IGetFeatureInfoParams) {
    return ({
        'SERVICE': 'WMS',
        'VERSION': VERSION,
        'REQUEST': 'GetFeatureInfo',
        'FORMAT': 'image/png',
        'TRANSPARENT': 'true',
        'QUERY_LAYERS': LAYERS,
        'LAYERS': LAYERS,
        'TILED': 'true',
        'STYLES': styles ?? null,
        'INFO_FORMAT': 'application/json',
        'feature_count': featureCount ?? 101,
        'CQL_FILTER': CQL_FILTER,
        'propertyName': propertyName,
        'dpi': getImageScale().dpi?.toString(),
        'buffer': buffer,
        'BBOX': bbox,
    })
};

export function customGetFeatureInfoUrl(wmsUrl: string, layersNames: string, crs: string, width: number, height: number, i: number, j: number, bbox: any, styles: any = '') {
    const url = wmsUrl +
        `?SERVICE=WMS&` +
        `VERSION=1.3.0&` +
        `REQUEST=GetFeatureInfo&` +
        `LAYERS=${layersNames}&` +
        `QUERY_LAYERS=${layersNames}&` +
        `BBOX=${bbox}&` +
        `CRS=${crs}&` +
        `WIDTH=${width}&` +
        `HEIGHT=${height}&` +
        `I=${i}&` +
        `J=${j}&` +
        `INFO_FORMAT=application/json&` +
        `FORMAT=image%2Fpng&` +
        `STYLES=${styles}`;
    return url
}

export const featurePropsForMapTooltip: any = {
    pathNormativitiesRoughness: 'Roughness,InfoObjectID,PathNormativityId',
    pathNormativitiesDefects: 'Defects,InfoObjectID,PathNormativityId',
    pathNormativitiesGaugeDepth: 'GaugeDepth,InfoObjectID,PathNormativityId',
    pathNormativitiesStrength: 'Strength,InfoObjectID,PathNormativityId',
    pathNormativitiesCohesion: 'Cohesion,InfoObjectID,PathNormativityId',
    pathNormativitiesTotal: 'Total,InfoObjectID,PathNormativityId',
    pathNormativitiesTotalGroup: 'Position,Ending,InfoObjectID,PathNormativityId',
    roadDiagnosticsSegments: 'Position,Ending,ExecutorOrgName,LengthAlongRoad,EventYear,EndDate,EventTypeString',
    roadCategorySegments: 'Position,Ending,LengthAlongRoad,RelativeCategoryId',
    roadBedLayerSegments: 'Position,Ending,LengthAlongRoad,RoadbedTypeID',
    highwayNetworkSegments: 'Position,Ending',
    roadWorkSegmentsPODD: 'Position,Ending,LengthAlongRoad,ExecutorOrgName,EventYear'
}